import React, { useEffect } from 'react';
import { graphql, PageRendererProps, Script } from 'gatsby';
import PagesLinkList from '../../components/PagesLinkList';

import { HeroSection, SEO } from '@library/shared-ui/';
import { HandleProcessStripeCheckout } from '../../utils/sezzlePro';
import { TemplateProps } from './type';
import { useDeviceType } from '../../utils/useDeviceType';
const PageSectionComponents = React.lazy(
  () => import('@library/shared-ui/components/PageSectionComponents')
);

const baseUrl = process.env.GATSBY_BASE_URL;

export const Head = ({ location, data, pageContext }) => {
  // every page schema
  const everyPageSchema = {
    '@context': 'https://schema.org/',
    '@type': 'Organization',
    url: 'https://sezzle.com/',
    logo: {
      '@type': 'ImageObject',
      url: 'https://images.ctfassets.net/6d085vujy22q/26U5cPa0so2bj8oWd9cl9r/ca403dcd85a002c62bd13dc1c806609d/Color-Logo.png?w=720&h=330&q=50&fm=png',
    },
    name: 'Sezzle',
    image: {
      '@type': 'ImageObject',
      url: 'https://images.ctfassets.net/6d085vujy22q/3d6zWuZVcQn7sjD8yC1vwf/cc8e1b37e404230e51c5b4248a85d42b/Group_220500005.png?w=718&h=483&q=50&fm=png',
    },
    description:
      'Pay in 4 and Build Credit. Welcome to Sezzle, The Responsible Way to Pay. Start Splitting your purchases into 4 installments over 6 weeks today. It’s our mission to financially empower the next generation of change-makers. Financial freedom is a right, not a privilege. Everyone deserves to be trusted, and by valuing, respecting, and supporting each individual, opportunities expand for everyone to achieve their potential. We’re here to help you achieve financial freedom and take control over your finances so you can build your future.',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      ratingCount: '210000',
    },
  };
  const everyPageSchemaAsString = JSON.stringify(everyPageSchema, null, 2);

  // https://sezzle.com/
  // home page schema
  const homePageSchema = {
    '@context': 'http://schema.org',
    '@type': 'WebSite',
    url: 'https://sezzle.com/',
    name: 'Sezzle',
    description:
      'Sezzle helps you buy what you want today and pay for over four equal installments. Get approved instantly with no credit check, and there are no fees if you pay on time.',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://sezzle.com/search/?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };
  const homePageSchemaAsString = JSON.stringify(homePageSchema, null, 2);

  // https://sezzle.com/app/
  // app schema
  const appSchema = {
    '@context': 'https://schema.org/',
    '@type': 'SoftwareApplication',
    name: 'Sezzle',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      ratingCount: '210000',
    },
    applicationCategory: 'FinanceApplication',
    author: {
      '@type': 'Person',
      name: 'Sezzle',
      url: 'https://sezzle.com/',
    },
    description:
      "Sezzle allows you to buy now and pay later! Shop now, get what you need, and pay later in 4 interest-free installment payments over six weeks. The App Is Where It's At: Download the Sezzle App to shop now and pay later from anywhere you go.",
    image:
      'https://images.ctfassets.net/6d085vujy22q/26U5cPa0so2bj8oWd9cl9r/ca403dcd85a002c62bd13dc1c806609d/Color-Logo.png?w=720&h=330&q=50&fm=png',
    url: 'https://sezzle.com/',
  };
  const appSchemaAsString = JSON.stringify(appSchema, null, 2);

  // app breadcrumblist schema
  const appBreadcrumbListSchema = {
    '@context': 'http://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: {
          '@id': 'https://sezzle.com/',
          name: 'Home',
        },
      },
      {
        '@type': 'ListItem',
        position: 2,
        item: {
          '@id': 'https://sezzle.com/app/',
          name: 'Get the App',
        },
      },
    ],
  };
  const appBreadcrumbListSchemaAsString = JSON.stringify(
    appBreadcrumbListSchema,
    null,
    2
  );

  // app WebSite schema
  const appWebSiteSchema = {
    '@context': 'http://schema.org',
    '@type': 'WebSite',
    url: 'https://sezzle.com/app/',
    name: 'Download the Sezzle App',
    description:
      'Download the top-rated Sezzle shop now pay later app to buy what you want today. Click to learn how to use our interest free installment payment app now!',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://sezzle.com/search/?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };
  const appWebSiteSchemaAsString = JSON.stringify(appWebSiteSchema, null, 2);

  // https://sezzle.com/how-it-works/
  // how it works online schema

  const howItWorksOnlineSchema = {
    '@context': 'https://schema.org/',
    '@type': 'HowTo',
    name: 'How to Sezzle-It Online',
    description:
      'Pay Later and Build Credit, Responsibly. Sezzle is committed to financially empowering the next generation, one payment at a time.',
    step: [
      {
        '@type': 'HowToStep',
        text: 'Shop your favorite brand/product',
        image:
          'https://images.ctfassets.net/6d085vujy22q/oqbhjfyKg2wOuqNl87Shp/99caacffe1d99c607facf765fcd39186/image_18__1_.png?w=1920&h=1169&q=50&fm=webp',
        itemListElement: [
          {
            '@type': 'HowToDirection',
            text: 'Shop your favorite brand/product',
          },
        ],
      },
      {
        '@type': 'HowToStep',
        text: "Choose 'Pay with Sezzle' at checkout",
        image:
          'https://images.ctfassets.net/6d085vujy22q/7pUzFUTndjOs8JXdDNuSQy/74f8e8ee7b74f88804aa452ea73f380f/Group_2192.png?w=1476&h=3050&q=50&fm=webp',
        itemListElement: [
          {
            '@type': 'HowToDirection',
            text: "Choose 'Pay with Sezzle' at checkout",
          },
        ],
      },
      {
        '@type': 'HowToStep',
        text: 'Select your payment plan',
        image:
          'https://images.ctfassets.net/6d085vujy22q/1cTI7UqFPf7yyejhLhyMHI/13af1543666ea35d728e96dee0a50d73/Group_2193.png?w=1472&h=3022&q=50&fm=webp',
        itemListElement: [
          {
            '@type': 'HowToDirection',
            text: 'Select your payment plan',
          },
        ],
      },
      {
        '@type': 'HowToStep',
        text: 'Complete your order',
        image:
          'https://images.ctfassets.net/6d085vujy22q/2mtRyEZZYOD5NI8bRz4N4F/90fa62cb755a2251c16b748139032407/Group_2195.png?w=1472&h=3022&q=50&fm=webp',
        itemListElement: [
          {
            '@type': 'HowToDirection',
            text: 'Complete your order',
          },
        ],
      },
    ],
  };
  const howItWorksOnlineSchemaAsString = JSON.stringify(
    howItWorksOnlineSchema,
    null,
    2
  );

  // how it works in-store schema
  const howItWorksInStoreSchema = {
    '@context': 'https://schema.org/',
    '@type': 'HowTo',
    name: 'How to Sezzle-It In-Store',
    description:
      'Pay Later and Build Credit, Responsibly. Sezzle is committed to financially empowering the next generation, one payment at a time.',
    step: [
      {
        '@type': 'HowToStep',
        text: 'Select Make a purchase request on the Virtual Card tab',
        image:
          'https://images.ctfassets.net/6d085vujy22q/AXwPPZx1kfspfIQ7LotPj/4d71a08055d38b0b92858bd5bbd75b97/iPhone_14___15_Pro_Max_-_5.png?w=1290&h=2796&q=50&fm=webp',
        itemListElement: [
          {
            '@type': 'HowToDirection',
            text: 'Select Make a purchase request on the Virtual Card tab',
          },
        ],
      },
      {
        '@type': 'HowToStep',
        text: 'Enter the amount you plan to spend and tap continue',
        image:
          'https://images.ctfassets.net/6d085vujy22q/5Tl2f4beZ3kvrh98YbJ0yp/41322d9d6c8730aecf8413c80dd5550d/iPhone_14___15_Pro_Max_-_6.png?w=1290&h=2796&q=50&fm=webp',
        itemListElement: [
          {
            '@type': 'HowToDirection',
            text: 'Enter the amount you plan to spend and tap continue',
          },
        ],
      },
      {
        '@type': 'HowToStep',
        text: 'Read the loan terms and continue if agreed',
        image:
          'https://images.ctfassets.net/6d085vujy22q/dJvXGLZIOsCft0ljg78Bg/9b4147cf2c04daeb5a2e12b864034481/Anywhere-eMail-Gif-4-02-27-20241-ezgif.com-crop__1_.gif?w=420&h=918&q=50&fm=webp',
        itemListElement: [
          {
            '@type': 'HowToDirection',
            text: 'Read the loan terms and continue if agreed',
          },
        ],
      },
      {
        '@type': 'HowToStep',
        text: 'Use the Virtual Card at merchant checkout or terminal',
        image:
          'https://images.ctfassets.net/6d085vujy22q/2mtRyEZZYOD5NI8bRz4N4F/90fa62cb755a2251c16b748139032407/Group_2195.png?w=1472&h=3022&q=50&fm=webp',
        itemListElement: [
          {
            '@type': 'HowToDirection',
            text: 'Use the Virtual Card at merchant checkout or terminal',
          },
        ],
      },
    ],
  };
  const howItWorksInStoreSchemaAsString = JSON.stringify(
    howItWorksInStoreSchema,
    null,
    2
  );

  const howItWorksBreadcrumbListSchema = {
    '@context': 'http://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: {
          '@id': 'https://sezzle.com/',
          name: 'Home',
        },
      },
      {
        '@type': 'ListItem',
        position: 2,
        item: {
          '@id': 'https://sezzle.com/how-it-works/',
          name: 'How to Sezzle',
        },
      },
    ],
  };
  const howItWorksBreadcrumbListSchemaAsString = JSON.stringify(
    howItWorksBreadcrumbListSchema,
    null,
    2
  );

  // how it works website schema
  const howItWorksWebSiteSchema = {
    '@context': 'http://schema.org',
    '@type': 'WebSite',
    url: 'https://sezzle.com/how-it-works/',
    name: 'Pay Later and Build Credit, Responsibly',
    description:
      'How does Sezzle Work? Split your order into 4 payments over 6 weeks! Where can I use Sezzle? Anywhere with the Sezzle virtual card! No credit check needed!',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://sezzle.com/search/?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const howItWorksWebSiteSchemaAsString = JSON.stringify(
    howItWorksWebSiteSchema,
    null,
    2
  );

  // https://sezzle.com/merchants/
  // merchants schema

  const merchantsBreadcrumbListSchema = {
    '@context': 'http://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: {
          '@id': 'https://sezzle.com/',
          name: 'Home',
        },
      },
      {
        '@type': 'ListItem',
        position: 2,
        item: {
          '@id': 'https://sezzle.com/merchants/',
          name: 'For Business',
        },
      },
    ],
  };
  const merchantsBreadcrumbListSchemaAsString = JSON.stringify(
    merchantsBreadcrumbListSchema,
    null,
    2
  );

  const merchantsWebSiteSchema = {
    '@context': 'http://schema.org',
    '@type': 'WebSite',
    url: 'https://sezzle.com/merchants/',
    name: 'Sezzle Merchants',
    description:
      'Explore partnership opportunities and benefits with Sezzle, empowering merchants with flexible payment options.',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://sezzle.com/search/?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };
  const merchantsWebSiteSchemaAsString = JSON.stringify(
    merchantsWebSiteSchema,
    null,
    2
  );

  return (
    <>
      <script type="application/ld+json">{everyPageSchemaAsString}</script>
      {pageContext.contentfulSlug === 'website-home-page' && (
        <script type="application/ld+json">{homePageSchemaAsString}</script>
      )}
      {pageContext.contentfulSlug === 'app' && (
        <script type="application/ld+json">{appSchemaAsString}</script>
      )}
      {pageContext.contentfulSlug === 'app' && (
        <script type="application/ld+json">
          {appBreadcrumbListSchemaAsString}
        </script>
      )}
      {pageContext.contentfulSlug === 'app' && (
        <script type="application/ld+json">{appWebSiteSchemaAsString}</script>
      )}
      {pageContext.contentfulSlug === 'how-it-works' && (
        <script type="application/ld+json">
          {howItWorksOnlineSchemaAsString}
        </script>
      )}
      {pageContext.contentfulSlug === 'how-it-works' && (
        <script type="application/ld+json">
          {howItWorksInStoreSchemaAsString}
        </script>
      )}
      {pageContext.contentfulSlug === 'how-it-works' && (
        <script type="application/ld+json">
          {howItWorksBreadcrumbListSchemaAsString}
        </script>
      )}
      {pageContext.contentfulSlug === 'how-it-works' && (
        <script type="application/ld+json">
          {howItWorksWebSiteSchemaAsString}
        </script>
      )}
      {pageContext.contentfulSlug === 'merchants' && (
        <script type="application/ld+json">
          {merchantsBreadcrumbListSchemaAsString}
        </script>
      )}
      {pageContext.contentfulSlug === 'merchants' && (
        <script type="application/ld+json">
          {merchantsWebSiteSchemaAsString}
        </script>
      )}
      <link
        rel="preconnect"
        href="https://dev.visualwebsiteoptimizer.com"
      />
      <SEO
        pageTitle={data?.contentfulPageTemplate?.seoPageTitle}
        pageDescription={data?.contentfulPageTemplate?.seoPageDescription}
        domain={`${baseUrl}${location.pathname}`}
        locale={pageContext.locale}
      />
    </>
  );
};

const ContentfulPageTemplate = ({ data, pageContext }: TemplateProps) => {
  const { pagesLinkList, slug } = pageContext;
  const smartScriptCampaign = React.useMemo(
    () => data?.contentfulPageTemplate?.smartScriptCampaign,
    [data?.contentfulPageTemplate]
  );

  const deviceType = useDeviceType();

  const heroSection = (
    <HeroSection
      callOut={data?.contentfulPageTemplate?.heroSection?.callOut}
      subNavBar={data?.contentfulPageTemplate?.heroSection?.subNavBar}
      hero={data?.contentfulPageTemplate?.heroSection?.hero}
      backgroundColor={
        data?.contentfulPageTemplate?.heroSection?.backgroundColor
      }
    />
  );

  const sections = data?.contentfulPageTemplate?.sections?.map((section) => (
    <React.Suspense fallback={<div />}>
      <PageSectionComponents
        sectionType={section.sys.contentType.sys.id}
        section={section}
        key={section.id}
        pageContext={pageContext}
      />
    </React.Suspense>
  ));

  const pagesList = (
    <PagesLinkList
      elements={pagesLinkList}
      slug={slug}
    />
  );

  useEffect(() => {
    // If the device type is not available (usually during SSR), do not redirect
    if (!deviceType) return;
    const allowedDevices = data?.contentfulPageTemplate?.allowedDevices;
    if (allowedDevices && !allowedDevices.includes(deviceType)) {
      window.location.href = data?.contentfulPageTemplate?.redirectUrl ?? '/';
    }
  }, [deviceType, data]);

  useEffect(() => {
    if (slug === 'sezzle-pro/success') {
      HandleProcessStripeCheckout();
    }
  }, [slug]);

  return (
    <>
      <input
        type="hidden"
        value={smartScriptCampaign}
        id="smart-script-campaign"
        readOnly
      />
      <Script
        key="appsflyersmartscript"
        src="/appsflyersmartscript.js"
        rel="preconnect"
      />
      {heroSection}
      {sections}
      {pagesList}
    </>
  );
};

export default ContentfulPageTemplate;

export const getAllPagesFromContentful = graphql`
  query Page($contentfulSlug: String!, $locale: String!) {
    contentfulPageTemplate(
      slug: { eq: $contentfulSlug }
      node_locale: { eq: $locale }
    ) {
      slug
      node_locale
      project
      seoPageTitle
      seoPageDescription
      replacements
      smartScriptCampaign
      allowedDevices
      redirectUrl
      navbarMenuSection {
        ...navigationBarAndMenuFields
      }
      footerSection {
        ...footerFields
      }
      heroSection {
        ...heroFields
      }
      sections {
        ...pageSectionFields
      }
    }
  }
`;
